"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

import { useRouter, useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  Save,
  X,
  RefreshCw,
  Upload,
  AlertTriangle,
  Copy,
} from "lucide-react";
import { TimeSlotPricingTab } from "@/components/show-details/TimeSlotPricingTab";
import { EmailsTab } from "@/components/show-details/EmailsTab";
import { ShowFAQsTab } from "@/components/show-details/ShowFAQsTab";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
// Removed RichTextEditor import - using raw HTML editor instead
import { toast } from "sonner";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { ROLES } from "@/lib/constants/roles";
import { useVenueData } from "@/contexts/venue-context";

import { MultiSelectEnhanced } from "@/components/ui/multi-select-enhanced";
import { AccountingArticleMapping } from "@/components/admin/shared/accounting-article-mapping";
import { ShowReviewQuestionsTab } from "@/components/admin/shows/show-review-questions-tab";

interface ShowDetailsClientProps {
  show: any;
  venueId: string;
  venue?: any;
  venueDefaultLanguage: string;
  customerTypes: any[];
  showStatuses: any[];
  userRoleId: number;
  availableLanguages: string[];
  venueStudios: any[];
  ruleSet?: any;
  isRoadShow?: boolean; // Flag to indicate if this is for road show settings
  /**
   * Features the user holds at this venue; tabs needing one they lack are
   * hidden. Omitted or null: every tab (super admin).
   */
  features?: string[] | null;
}

/** Tabs that need a feature beyond `shows` (lib/constants/features.ts). */
const TAB_FEATURES: Record<string, string> = {
  "time-slot-pricing": "pricing",
  rules: "rule_sets",
  emails: "email_templates",
  "review-questions": "review_questions",
  faqs: "faqs",
};

interface MediaItem {
  id: string;
  url: string;
  type: "image" | "video";
  name?: string;
  is_primary?: boolean;
  language?: string;
}

interface PricePoint {
  id: number;
  amount: number;
  label?: string;
  currency: string;
  vatPercentage: number;
  includeVat: boolean;
  createdAt?: string;
  status?: "active" | "inactive" | "archived";
  status_id?: number;
  pricing_id?: number;
  pricing?: {
    id: number;
    price: number;
    label?: string;
    currency?: string;
    vat_percentage: number;
  };
}

interface SlotPricing {
  timeSlotId: string;
  pricePointId: number | null;
  dayOfWeek: number;
}

export function ShowDetailsClient({
  show,
  venueId,
  venue: venueFromProps,
  venueDefaultLanguage,
  customerTypes,
  showStatuses,
  userRoleId,
  availableLanguages,
  venueStudios,
  ruleSet,
  isRoadShow = false,
  features = null,
}: ShowDetailsClientProps) {
  const canTab = (tab: string) =>
    !features || !TAB_FEATURES[tab] || features.includes(TAB_FEATURES[tab]);
  // Get venue data from context or use passed prop
  const { venue: venueFromContext } = useVenueData();
  const venue = venueFromProps || venueFromContext;
  // Use the studios passed from server instead of cached context
  const studios = venueStudios;
  const isLoadingStudios = false; // We already have the data from server
  const router = useRouter();
  const searchParams = useSearchParams();

  // Determine the portal path based on user role
  const portalPath =
    userRoleId === ROLES.SUPER_ADMIN ? "/superadmin" : "/booking";
  const [activeTab, setActiveTab] = useState("general");
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingBasicDetails, setIsSavingBasicDetails] = useState(false);
  const [isSavingRules, setIsSavingRules] = useState(false);
  const [isSavingMedia, setIsSavingMedia] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeLanguageTab, setActiveLanguageTab] = useState<string>(
    venue?.default_language || availableLanguages[0] || "en",
  );
  const [enabledLanguages] = useState<string[]>(availableLanguages);

  // Form data state
  const [formData, setFormData] = useState({
    name: {} as Record<string, string>,
    subtitle: {} as Record<string, string>,
    description: {} as Record<string, string>,
    duration: "",
    minParticipants: 0,
    maxParticipants: 0,
    minChargeParticipants: 0,
    basePrice: 0,
    showType: "on_site" as "on_site" | "on_road",
    visibleFrom: "",
    visibleUntil: "",
    selectedStudios: [] as string[],
    tags: [] as string[],

    customerTypeId: 1,
    statusId: 1,
    cutOffDays: 7, // Default 7 days for reservation cut-off

    // Rule Sets - Booking Modification & Cancellation Rules
    partChangeFullRefundDays: 7,
    partChangeCutoffHours: 5,
    changeTimeslotDays: 7,
    cancelFreeDays: 7,
    cancelGraceHours: 24,
  });

  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  // Rule Sets state - track original values to detect changes
  const [originalRuleSet, setOriginalRuleSet] = useState<any>(null);

  // Time slot pricing state
  const [pricePoints, setPricePoints] = useState<PricePoint[]>([]);
  const [slotPricing, setSlotPricing] = useState<SlotPricing[]>([]);
  const [selectedPricePoint, setSelectedPricePoint] = useState<number | null>(
    null,
  );
  const [isSavingSlots, setIsSavingSlots] = useState(false);
  const [savingSlotIds, setSavingSlotIds] = useState<Set<string>>(new Set());
  const [pricingViewRoleId, setPricingViewRoleId] = useState<number>(
    ROLES.BOOKING_STAFF,
  );

  // Predefined tags with ability to add custom ones
  const [availableTags, setAvailableTags] = useState([
    "Mystery",
    "Teamwork",
    "Thriller",
    "VR",
    "Gaming",
    "Technology",
    "Cocktails",
    "Social",
    "Learning",
    "Team Building",
    "Corporate",
    "Challenge",
    "Escape Room",
    "Puzzle",
    "Science",
    "Wine",
    "Tasting",
    "Action",
    "Sport",
    "Competition",
    "Cooking",
    "Food",
  ]);

  // Check for tab parameter in URL
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      tab &&
      ["general", "multilingual", "time-slot-pricing", "emails"].includes(tab)
    ) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Refresh venue data (including studios) when component mounts or venueId changes
  // Log studios data for debugging
  useEffect(() => {
    console.log("Studios from server:", studios.length, "studios loaded");
  }, [studios]);

  // Initialize form data with show data
  useEffect(() => {
    if (show) {
      // Initialize multilingual data
      const names: Record<string, string> = {};
      const subtitles: Record<string, string> = {};
      const descriptions: Record<string, string> = {};

      // Process show_infos for multilingual data
      if (show.show_infos) {
        show.show_infos.forEach((info: any) => {
          names[info.lang] = info.name || "";
          subtitles[info.lang] = info.subtitle || "";
          descriptions[info.lang] = info.desc || "";
        });
      }

      // Get selected studio IDs
      const selectedStudios =
        show.show_studios?.map((ss: any) => ss.studio_id.toString()) || [];

      // Debug studio data
      console.log("ShowDetailsClient studio debug:", {
        show_studios: show.show_studios,
        selectedStudios,
        contextStudios: studios.map((s) => ({ id: s.id, name: s.name })),
      });

      setFormData({
        name: names,
        subtitle: subtitles,
        description: descriptions,

        duration: show.duration_minutes ? `${show.duration_minutes}` : "60",

        minParticipants: show.min_participants || 2,
        maxParticipants: show.max_participants || 10,
        minChargeParticipants: show.min_charge_participants || 0,
        basePrice: show.base_price ?? 0,
        showType: show.show_type === "on_road" ? "on_road" : "on_site",
        visibleFrom: show.visible_from || "",
        visibleUntil: show.visible_until || "",
        selectedStudios: selectedStudios,
        tags: [], // TODO: Implement tags

        customerTypeId: show.customer_type_id || 1,
        statusId: show.status_id || 1,
        cutOffDays: show.cut_off_days ?? 7, // Initialize with existing value or default to 7 (allows 0 when explicitly set)

        // Rule Sets - will be initialized separately when ruleSet prop changes
        partChangeFullRefundDays: 7,
        partChangeCutoffHours: 5,
        changeTimeslotDays: 7,
        cancelFreeDays: 7,
        cancelGraceHours: 24,
      });

      // Initialize media items
      if (show.show_medias) {
        setMediaItems(
          show.show_medias.map((media: any) => ({
            id: media.id.toString(),
            url: media.url,
            type: media.type as "image" | "video",

            name: `Media ${media.id}`,
            is_primary: media.is_primary,
            language: media.lang || "en",
          })),
        );
      }

      // Initialize price points from show data
      if (show.show_price_points) {
        console.log("Loading price points from DB:", show.show_price_points);
        const loadedPricePoints = show.show_price_points.map((pp: any) => ({
          id: pp.id,
          amount: pp.pricing?.price || pp.amount,
          label: pp.pricing?.label || pp.label,
          currency: pp.pricing?.currency || pp.currency || "SEK",
          vatPercentage: pp.pricing?.vat_percentage || pp.vat_percentage || 0,
          pricing_id: pp.pricing_id,
          pricing: pp.pricing,
          includeVat: pp.include_vat !== false,
          createdAt: pp.created_at,
          status:
            pp.status_id === STATUS_IDS.PRICE_POINT_ACTIVE
              ? "active"
              : pp.status_id === STATUS_IDS.PRICE_POINT_ARCHIVED
                ? "archived"
                : "active",
          status_id: pp.status_id || STATUS_IDS.PRICE_POINT_ACTIVE, // Default to active if no status
        }));
        console.log("Mapped price points:", loadedPricePoints);
        setPricePoints(loadedPricePoints);
      }

      // Initialize slot pricing from show data
      if (show.show_time_slot_pricing) {
        console.log(
          "Loading time slot pricing from DB:",
          show.show_time_slot_pricing,
        );
        const loadedSlotPricing = show.show_time_slot_pricing.map((sp: any) => {
          // Handle time format - database might return "13:00:00", we need "13:00"
          const timeSlot = sp.time_slot.substring(0, 5); // Get only HH:MM part
          return {
            timeSlotId: `${sp.day_of_week}_${timeSlot}`,
            pricePointId: sp.price_point_id,
            dayOfWeek: sp.day_of_week,
            user_role_id: sp.user_role_id,
          };
        });
        console.log("Mapped slot pricing:", loadedSlotPricing);
        setSlotPricing(loadedSlotPricing);
      }
    }
  }, [show]);

  // Initialize rule sets from prop
  useEffect(() => {
    if (ruleSet) {
      console.log("Initializing rule sets from prop:", ruleSet);
      setFormData((prev) => ({
        ...prev,
        partChangeFullRefundDays: ruleSet.part_change_full_refund_days || 7,
        partChangeCutoffHours: ruleSet.part_change_cutoff_hours || 5,
        changeTimeslotDays: ruleSet.change_timeslot_days ?? 7,
        cancelFreeDays: ruleSet.cancel_free_days || 7,
        cancelGraceHours: ruleSet.cancel_grace_hours || 24,
      }));
      setOriginalRuleSet(ruleSet);
    }
  }, [ruleSet]);

  // Check for tab parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, []);

  // Save basic details (info, settings, studios)
  const saveBasicDetails = async () => {
    setIsSavingBasicDetails(true);

    try {
      // Validate studio assignment — required for on-site shows only. Road shows
      // happen at the customer's site, so a studio is optional.
      if (
        formData.showType !== "on_road" &&
        (!formData.selectedStudios || formData.selectedStudios.length === 0)
      ) {
        toast.error("Please select at least one studio for this show");
        setIsSavingBasicDetails(false);
        return;
      }

      // Extract duration in minutes
      const durationMatch = formData.duration.match(/(\d+)/);
      const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 60;

      // Prepare show infos for update
      const showInfos = Object.entries(formData.name).map(([lang, name]) => ({
        lang,
        name,
        subtitle: formData.subtitle[lang] || "",
        description: formData.description[lang] || "",
      }));

      // Prepare studio assignments

      const studioAssignments = formData.selectedStudios.map((studioId) => ({
        studio_id: parseInt(studioId),
      }));

      const requestBody = {
        duration_minutes: durationMinutes,
        min_participants: formData.minParticipants,
        max_participants: formData.maxParticipants,
        min_charge_participants: formData.minChargeParticipants || null,
        customer_type_id: formData.customerTypeId,
        status_id: formData.statusId,
        cut_off_days: formData.cutOffDays, // Add cut-off days for reservation deadline
        visible_from: formData.visibleFrom || null,
        visible_until: formData.visibleUntil || null,
        showInfos: showInfos,
        studioAssignments: studioAssignments,
        // Fixed base price stored directly on the shows table (defaults to 0).
        base_price: formData.basePrice || 0,
        // Show type (on_site | on_road) — editable here so a show can be
        // switched between an on-site show and a road show.
        show_type: formData.showType,
      };

      console.log("Saving basic details:", requestBody);

      const response = await fetch(`/api/venues/${venueId}/shows/${show.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update show details";
        let statusReverted = false;
        try {
          const errorData = await response.json();

          // Build a clear error message
          errorMessage = errorData.message || errorData.error || errorMessage;
          statusReverted = errorData.statusReverted || false;

          // Add missing requirements as a readable list
          if (
            errorData.missingRequirements &&
            Array.isArray(errorData.missingRequirements)
          ) {
            const requirements = errorData.missingRequirements.join(", ");
            errorMessage = `${errorMessage}\n\nMissing: ${requirements}`;
          }

          // If status was reverted, refresh the page to show the reverted status
          if (statusReverted) {
            toast.error(errorMessage);
            router.refresh();
            setIsSavingBasicDetails(false);
            return;
          }
        } catch (jsonError) {
          errorMessage = `Failed to update show details (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      // Check if rule sets have changed and save them
      const ruleSetsChanged =
        !originalRuleSet ||
        originalRuleSet.part_change_full_refund_days !==
          formData.partChangeFullRefundDays ||
        originalRuleSet.part_change_cutoff_hours !==
          formData.partChangeCutoffHours ||
        (originalRuleSet.change_timeslot_days ?? 7) !==
          formData.changeTimeslotDays ||
        originalRuleSet.cancel_free_days !== formData.cancelFreeDays ||
        originalRuleSet.cancel_grace_hours !== formData.cancelGraceHours;

      if (ruleSetsChanged) {
        const ruleSetPayload = {
          partChangeFullRefundDays: formData.partChangeFullRefundDays,
          partChangeCutoffHours: formData.partChangeCutoffHours,
          changeTimeslotDays: formData.changeTimeslotDays,
          cancelFreeDays: formData.cancelFreeDays,
          cancelGraceHours: formData.cancelGraceHours,
          customerTypeId: formData.customerTypeId,
        };

        console.log(
          "Rule sets changed, saving to:",
          `/api/venues/${venueId}/shows/${show.id}/rule-sets`,
        );
        console.log(
          "Rule set payload:",
          JSON.stringify(ruleSetPayload, null, 2),
        );

        // Save rule sets (API will handle archive-and-create pattern)
        const ruleSetResponse = await fetch(
          `/api/venues/${venueId}/shows/${show.id}/rule-sets`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(ruleSetPayload),
          },
        );

        console.log(
          "Rule set response status:",
          ruleSetResponse.status,
          ruleSetResponse.statusText,
        );

        if (!ruleSetResponse.ok) {
          const errorData = await ruleSetResponse.json();
          console.error(
            "Failed to save rule sets - Status:",
            ruleSetResponse.status,
          );
          console.error("Failed to save rule sets - Error data:", errorData);
          toast.error(
            `Rule sets update failed: ${errorData.error || "Unknown error"}`,
          );
          // Don't show success toast if rule sets failed
          return;
        }

        const successData = await ruleSetResponse.json();
        console.log("Rule sets saved successfully:", successData);
      }

      // Show unified success message after all operations complete
      toast.success("Show details updated successfully");

      // Remove the 'new' parameter from URL after first save
      if (searchParams.get("new") === "true") {
        const newUrl = `${portalPath}/venue/${venueId}/shows/${show.id}`;
        router.replace(newUrl);
      } else {
        // Optional: refresh the page to get latest server data
        router.refresh();
      }
    } catch (error) {
      console.error("Error saving show details:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save show details",
      );
    } finally {
      setIsSavingBasicDetails(false);
    }
  };

  // Save rule sets only
  const saveRules = async () => {
    setIsSavingRules(true);
    try {
      const ruleSetPayload = {
        partChangeFullRefundDays: formData.partChangeFullRefundDays,
        partChangeCutoffHours: formData.partChangeCutoffHours,
        changeTimeslotDays: formData.changeTimeslotDays,
        cancelFreeDays: formData.cancelFreeDays,
        cancelGraceHours: formData.cancelGraceHours,
        customerTypeId: formData.customerTypeId,
      };

      const ruleSetResponse = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/rule-sets`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(ruleSetPayload),
        },
      );

      if (!ruleSetResponse.ok) {
        const errorData = await ruleSetResponse.json();
        throw new Error(errorData.error || "Failed to save rule sets");
      }

      // Update originalRuleSet to reflect the saved values
      setOriginalRuleSet({
        part_change_full_refund_days: formData.partChangeFullRefundDays,
        part_change_cutoff_hours: formData.partChangeCutoffHours,
        change_timeslot_days: formData.changeTimeslotDays,
        cancel_free_days: formData.cancelFreeDays,
        cancel_grace_hours: formData.cancelGraceHours,
      });

      toast.success("Rule sets saved successfully");
      router.refresh();
    } catch (error) {
      console.error("Error saving rule sets:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save rule sets",
      );
    } finally {
      setIsSavingRules(false);
    }
  };

  // Save media only
  const saveMedia = async () => {
    setIsSavingMedia(true);
    try {
      // Prepare media items for update - maintain order per language
      const mediaByLanguage = mediaItems.reduce(
        (acc, item) => {
          const lang = item.language || "en";
          if (!acc[lang]) acc[lang] = [];
          acc[lang].push(item);
          return acc;
        },
        {} as Record<string, MediaItem[]>,
      );

      const mediaData = Object.entries(mediaByLanguage).flatMap(([, items]) =>
        items.map((item, index) => ({
          url: item.url,
          type: item.type,
          is_primary: item.is_primary || false,
          order: index, // Order is per language
          language: item.language || "en",
        })),
      );

      // Include show infos to preserve subtitle and description
      const showInfos = Object.entries(formData.name).map(([lang, name]) => ({
        lang,
        name,
        subtitle: formData.subtitle[lang] || "",
        description: formData.description[lang] || "",
      }));

      const requestBody = {
        media: mediaData,
        showInfos: showInfos, // Include showInfos to preserve content
      };

      console.log("Saving media:", requestBody);

      const response = await fetch(`/api/venues/${venueId}/shows/${show.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update media";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (jsonError) {
          errorMessage = `Failed to update media (${response.status} ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      toast.success("Media updated successfully");

      // Remove the 'new' query parameter after successful save
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.has("new")) {
        currentUrl.searchParams.delete("new");
        router.replace(currentUrl.pathname + currentUrl.search);
      } else {
        router.refresh();
      }
    } catch (error) {
      console.error("Error saving media:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save media",
      );
    } finally {
      setIsSavingMedia(false);
    }
  };

  // Generate 15-minute interval time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour < 23; hour++) {
      // 6 AM to 10:45 PM
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const uniqueTimes = generateTimeSlots();

  // Auto-save single time slot pricing
  const saveSingleTimeSlot = async (
    timeSlotId: string,
    pricePointId: number | null,
    showToast: boolean = true,
    userRoleId?: number,
  ) => {
    try {
      const existingSlot = slotPricing.find(
        (sp) => sp.timeSlotId === timeSlotId,
      );
      const roleIdToUse = userRoleId || ROLES.GUEST_USER;

      const payload = {
        timeSlotId,
        pricePointId,
        action: pricePointId ? "add" : "remove",
        user_role_id: roleIdToUse,
      };

      console.log("saveSingleTimeSlot debug:", {
        timeSlotId,
        pricePointId,
        userRoleId,
        existingSlot,
        roleIdToUse,
        payload,
      });

      const response = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/time-slot-pricing/single`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save time slot pricing");
      }

      if (showToast) {
        toast.success("Time slot pricing updated");
      }
    } catch (error) {
      console.error("Error saving time slot pricing:", error);
      if (showToast) {
        toast.error("Failed to save time slot pricing");
      }
      throw error; // Re-throw to handle in bulk operations
    }
  };

  // Auto-save time slot pricing
  const saveTimeSlotPricing = async (updatedSlotPricing: SlotPricing[]) => {
    console.log(
      "saveTimeSlotPricing called with:",
      updatedSlotPricing.length,
      "slots",
    );

    // Since TimeSlotPricingTab now manages its own state, we just need to save to API
    // No need to compare with stale parent state

    setSavingSlotIds(new Set());
    setIsSavingSlots(true);

    try {
      // Use bulk endpoint for all changes
      const response = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/time-slot-pricing`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ slotPricing: updatedSlotPricing }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save time slot pricing");
      }

      // Show simple success message
      toast.success("Time slot pricing updated successfully");
    } catch (error) {
      console.error("Error saving time slot pricing:", error);
      toast.error("Failed to save time slot pricing");
      throw error; // Re-throw so TimeSlotPricingTab can handle it
    } finally {
      setIsSavingSlots(false);
      setSavingSlotIds(new Set());
    }
  };

  // Create price point
  const createPricePoint = async (newPoint: any) => {
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/price-points`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newPoint),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create price point");
      }

      const { pricePoint } = await response.json();

      // Add to local state
      const formattedPricePoint: PricePoint = {
        id: pricePoint.id,
        amount: pricePoint.pricing?.price || pricePoint.amount,
        label: pricePoint.pricing?.label || pricePoint.label,
        currency: pricePoint.pricing?.currency || pricePoint.currency || "SEK",
        vatPercentage:
          pricePoint.pricing?.vat_percentage || pricePoint.vat_percentage || 0,
        includeVat: pricePoint.include_vat,
        createdAt: pricePoint.created_at,
        status: "active",
        pricing_id: pricePoint.pricing_id,
        pricing: pricePoint.pricing,
      };

      setPricePoints([...pricePoints, formattedPricePoint]);
      setSelectedPricePoint(pricePoint.id);
      toast.success("Price point created");
    } catch (error) {
      console.error("Error creating price point:", error);
      toast.error("Failed to create price point");
    }
  };

  // Archive price point (instead of delete)
  const deletePricePoint = async (id: number) => {
    try {
      // Archive by updating status instead of deleting
      const response = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/price-points/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ statusId: STATUS_IDS.PRICE_POINT_ARCHIVED }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to archive price point");
      }

      const { pricePoint } = await response.json();

      // Update local state
      setPricePoints(
        pricePoints.map((p) =>
          p.id === id
            ? { ...p, status_id: pricePoint.status_id, status: "archived" }
            : p,
        ),
      );

      // If the archived price point was selected, deselect it
      if (selectedPricePoint === id) {
        setSelectedPricePoint(null);
      }

      toast.success("Price point archived");
    } catch (error) {
      console.error("Error archiving price point:", error);
      toast.error("Failed to archive price point");
    }
  };

  // Update price point status
  const updatePricePointStatus = async (id: number, statusId: number) => {
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shows/${show.id}/price-points/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ statusId }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update price point status");
      }

      const { pricePoint } = await response.json();

      // Update local state
      setPricePoints(
        pricePoints.map((p) =>
          p.id === id
            ? {
                ...p,
                status_id: pricePoint.status_id,
                status:
                  pricePoint.status_id === STATUS_IDS.PRICE_POINT_ACTIVE
                    ? "active"
                    : "archived",
              }
            : p,
        ),
      );

      // If the price point becomes archived and it's selected, deselect it
      if (
        selectedPricePoint === id &&
        pricePoint.status_id === STATUS_IDS.PRICE_POINT_ARCHIVED
      ) {
        setSelectedPricePoint(null);
      }

      toast.success("Price point status updated");
    } catch (error) {
      console.error("Error updating price point status:", error);
      toast.error("Failed to update price point status");
    }
  };

  // Media handling functions

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setIsUploadingMedia(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          // Upload to Supabase storage
          const formData = new FormData();
          formData.append("file", file);
          formData.append("bucket", "show-media");

          const response = await fetch("/api/upload", {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            const error = await response.json();
            toast.error(`Failed to upload ${file.name}: ${error.error}`);
            failCount++;
            continue;
          }

          const { url } = await response.json();

          const newItem: MediaItem = {
            id: `${Date.now()}_${i}`,
            url: url,
            type: file.type.startsWith("image/") ? "image" : "video",
            name: file.name,
            is_primary:
              mediaItems.filter((item) => item.language === activeLanguageTab)
                .length === 0 && i === 0,
            language: activeLanguageTab,
          };
          setMediaItems((prev) => [...prev, newItem]);
          successCount++;
        } else {
          toast.error(
            `Unsupported file type: ${file.name}. Please upload images or videos only.`,
          );
          failCount++;
        }
      }

      // Only show success if at least one file was uploaded successfully
      if (successCount > 0) {
        if (failCount > 0) {
          toast.success(
            `${successCount} file(s) uploaded successfully. ${failCount} failed.`,
          );
        } else {
          toast.success("Media uploaded successfully");
        }
      } else if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} file(s)`);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to upload media",
      );
    } finally {
      setIsUploadingMedia(false);
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    setIsUploadingMedia(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          // Upload to Supabase storage
          const formData = new FormData();
          formData.append("file", file);
          formData.append("bucket", "show-media");

          const response = await fetch("/api/upload", {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            const error = await response.json();
            toast.error(`Failed to upload ${file.name}: ${error.error}`);
            failCount++;
            continue;
          }

          const { url } = await response.json();

          const newItem: MediaItem = {
            id: `${Date.now()}_${i}`,
            url: url,
            type: file.type.startsWith("image/") ? "image" : "video",
            name: file.name,
            is_primary:
              mediaItems.filter((item) => item.language === activeLanguageTab)
                .length === 0 && i === 0,
            language: activeLanguageTab,
          };
          setMediaItems((prev) => [...prev, newItem]);
          successCount++;
        } else {
          toast.error(
            `Unsupported file type: ${file.name}. Please upload images or videos only.`,
          );
          failCount++;
        }
      }

      // Only show success if at least one file was uploaded successfully
      if (successCount > 0) {
        if (failCount > 0) {
          toast.success(
            `${successCount} file(s) uploaded successfully. ${failCount} failed.`,
          );
        } else {
          toast.success("Media uploaded successfully");
        }
      } else if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} file(s)`);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to upload media",
      );
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const removeMedia = (id: string) => {
    setMediaItems((prev) => prev.filter((item) => item.id !== id));
  };

  const setPrimaryMedia = (id: string) => {
    const selectedItem = mediaItems.find((item) => item.id === id);
    if (!selectedItem) return;

    setMediaItems((prev) =>
      prev.map((item) => ({
        ...item,
        // Set primary only for the selected item, remove primary from other items in the same language
        is_primary:
          item.id === id
            ? true
            : item.language === selectedItem.language
              ? false
              : item.is_primary,
      })),
    );
  };

  // Media drag and drop handlers for reordering
  const handleMediaDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleMediaDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleMediaDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();

    if (!draggedItemId || draggedItemId === targetId) return;

    const draggedItem = mediaItems.find((item) => item.id === draggedItemId);
    const targetItem = mediaItems.find((item) => item.id === targetId);

    if (!draggedItem || !targetItem) return;

    // Only allow reordering within the same language
    if (draggedItem.language !== targetItem.language) return;

    const draggedIndex = mediaItems.findIndex(
      (item) => item.id === draggedItemId,
    );
    const targetIndex = mediaItems.findIndex((item) => item.id === targetId);

    const newItems = [...mediaItems];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    setMediaItems(newItems);
  };

  const handleMediaDragEnd = () => {
    setDraggedItemId(null);
  };

  // Get show name for header
  const showName =
    show.show_infos?.find((info: any) => info.lang === "en")?.name ||
    show.show_infos?.[0]?.name ||
    "Unnamed show";

  const showSubtitle =
    show.show_infos?.find((info: any) => info.lang === "en")?.subtitle || "";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link href={`${portalPath}/venue/${venueId}/shows`}>
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold">{showName}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      show.statuses?.name === "active"
                        ? "bg-green-100 text-green-800"
                        : show.statuses?.name === "inactive"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {show.statuses?.name === "active"
                      ? "Active"
                      : show.statuses?.name === "inactive"
                        ? "Inactive"
                        : show.statuses?.name === "archived"
                          ? "Archived"
                          : show.statuses?.name || "Unknown"}
                  </span>
                  {isRoadShow && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      Road show
                    </span>
                  )}
                  <span className="text-gray-600">{showSubtitle}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Road shows reuse the standard show settings page; some features
              here are built for on-site shows and may not apply. */}
          {isRoadShow && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-500" />
              <p>
                This is a <strong>road show</strong>. It uses the same settings
                page as on-site shows, so some features (studios, time-slot
                pricing, on-site rules) may not apply or work as expected.
              </p>
            </div>
          )}

          {/* Show Details Form */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              {/* Tabs */}
              <div className="border-b mb-6">
                <nav className="-mb-px flex space-x-8">
                  {
                    <>
                      <button
                        onClick={() => setActiveTab("general")}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === "general"
                            ? "border-purple-600 text-purple-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                        suppressHydrationWarning
                      >
                        General settings
                      </button>
                      <button
                        onClick={() => setActiveTab("multilingual")}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === "multilingual"
                            ? "border-purple-600 text-purple-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                        suppressHydrationWarning
                      >
                        Content
                      </button>
                      {canTab("time-slot-pricing") && (
                        <button
                          onClick={() => setActiveTab("time-slot-pricing")}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === "time-slot-pricing"
                              ? "border-purple-600 text-purple-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                          suppressHydrationWarning
                        >
                          Time slot pricing
                        </button>
                      )}
                    </>
                  }
                  {canTab("emails") && (
                    <button
                      onClick={() => setActiveTab("emails")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === "emails"
                          ? "border-purple-600 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      suppressHydrationWarning
                    >
                      Email templates
                    </button>
                  )}
                  {canTab("review-questions") && (
                    <button
                      onClick={() => setActiveTab("review-questions")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === "review-questions"
                          ? "border-purple-600 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      suppressHydrationWarning
                    >
                      Review questions
                    </button>
                  )}
                  {!isRoadShow && (
                    <>
                      {canTab("rules") && (
                        <button
                          onClick={() => setActiveTab("rules")}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === "rules"
                              ? "border-purple-600 text-purple-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                          suppressHydrationWarning
                        >
                          Rules
                        </button>
                      )}
                      {canTab("faqs") && (
                        <button
                          onClick={() => setActiveTab("faqs")}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === "faqs"
                              ? "border-purple-600 text-purple-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                          suppressHydrationWarning
                        >
                          FAQs
                        </button>
                      )}
                    </>
                  )}
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === "general" && (
                <div className="space-y-6">
                  {/* Show Settings Section */}
                  <div className="border rounded-lg p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="customerType"
                          className="text-sm font-medium text-gray-700"
                        >
                          Customer type *
                        </Label>
                        <select
                          id="customerType"
                          value={formData.customerTypeId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customerTypeId: parseInt(e.target.value),
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          suppressHydrationWarning
                        >
                          {customerTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name === "corporate"
                                ? "Corporate"
                                : "Private"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="duration"
                          className="text-sm font-medium text-gray-700"
                        >
                          Duration (min) *
                        </Label>
                        <Input
                          id="duration"
                          type="number"
                          min="1"
                          value={formData.duration}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              duration: e.target.value,
                            })
                          }
                          placeholder="90"
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="minParticipants"
                          className="text-sm font-medium text-gray-700"
                        >
                          Min participants *
                        </Label>
                        <Input
                          id="minParticipants"
                          type="number"
                          min="1"
                          value={formData.minParticipants}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              minParticipants: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="e.g., 4"
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="maxParticipants"
                          className="text-sm font-medium text-gray-700"
                        >
                          Max participants *
                        </Label>
                        <Input
                          id="maxParticipants"
                          type="number"
                          min="1"
                          value={formData.maxParticipants}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              maxParticipants: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="e.g., 8"
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="minChargeParticipants"
                          className="text-sm font-medium text-gray-700"
                        >
                          Min charge participants
                        </Label>
                        <Input
                          id="minChargeParticipants"
                          type="number"
                          min="0"
                          value={formData.minChargeParticipants}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              minChargeParticipants:
                                parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="e.g., 4 (leave 0 to use Min Participants)"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Minimum number of participants used for charge
                          calculation.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="cutOffDays"
                          className="text-sm font-medium text-gray-700"
                        >
                          Cut-off days for reservations *
                        </Label>
                        <Input
                          id="cutOffDays"
                          type="number"
                          min="0"
                          value={formData.cutOffDays}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              cutOffDays:
                                e.target.value === ""
                                  ? 7
                                  : parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="e.g., 7"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Number of days before the show date when customer
                          reservations are cut off.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="studios"
                        className="text-sm font-medium text-gray-700"
                      >
                        Studio assignments *
                      </Label>
                      <MultiSelectEnhanced
                        options={(() => {
                          console.log("MultiSelect debug:", {
                            studiosLength: studios.length,
                            studiosData: studios.map((s) => ({
                              id: s.id,
                              name: s.name,
                              status_id: s.status_id,
                            })),
                            showStudiosData: show.show_studios?.map(
                              (ss: any) => ({
                                studio_id: ss.studio_id,
                                studio_name: ss.studios?.name,
                              }),
                            ),
                            STATUS_IDS_STUDIO_ACTIVE: STATUS_IDS.STUDIO_ACTIVE,
                          });

                          if (studios.length > 0) {
                            // Use context studios. Only list studios whose type
                            // matches the show: road shows show on_road studios,
                            // on-site shows show on_site studios.
                            return studios
                              .filter(
                                (studio) =>
                                  studio.status_id ===
                                    STATUS_IDS.STUDIO_ACTIVE &&
                                  (studio as any).type === formData.showType,
                              )
                              .map((studio) => ({
                                value: studio.id.toString(),
                                label: studio.name
                                  ? `${studio.name} (${studio.capacity || 0} people)`
                                  : `Studio ${studio.id}`,
                              }));
                          } else {
                            // Fallback: use show_studios data
                            return (show.show_studios || [])
                              .filter(
                                (ss: any) =>
                                  ss.studios?.status_id ===
                                    STATUS_IDS.STUDIO_ACTIVE &&
                                  ss.studios?.type === formData.showType,
                              )
                              .map((ss: any) => ({
                                value: ss.studio_id.toString(),
                                label: ss.studios?.name
                                  ? `${ss.studios.name} (${ss.studios.capacity || 0} people)`
                                  : `Studio ${ss.studio_id}`,
                              }));
                          }
                        })()}
                        selected={formData.selectedStudios}
                        onChange={(selectedIds) =>
                          setFormData({
                            ...formData,
                            selectedStudios: selectedIds,
                          })
                        }
                        placeholder="Select studios that can host this show..."
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500">
                        Choose which studios are suitable for hosting this show
                        {formData.showType === "on_road"
                          ? " (optional for road shows)"
                          : " (required)"}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="showType"
                          className="text-sm font-medium text-gray-700"
                        >
                          Type *
                        </Label>
                        <select
                          id="showType"
                          value={formData.showType}
                          onChange={(e) => {
                            const newType = e.target.value as
                              | "on_site"
                              | "on_road";
                            setFormData((prev) => ({
                              ...prev,
                              showType: newType,
                              // Drop selected studios that don't match the new
                              // type so a wrong-type studio can't be saved.
                              selectedStudios: prev.selectedStudios.filter(
                                (id) =>
                                  studios.some(
                                    (s) =>
                                      s.id.toString() === id &&
                                      (s as any).type === newType,
                                  ),
                              ),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          suppressHydrationWarning
                        >
                          <option value="on_site">On-site show</option>
                          <option value="on_road">Road show</option>
                        </select>
                        <p className="text-xs text-gray-500">
                          Road shows reuse this page; some on-site features may
                          not apply.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="basePrice"
                          className="text-sm font-medium text-gray-700"
                        >
                          Base price (excl. VAT)
                        </Label>
                        <Input
                          id="basePrice"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.basePrice}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              basePrice: parseFloat(e.target.value) || 0,
                            })
                          }
                          placeholder="e.g., 0"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Fixed base price for the show (defaults to 0).
                          {isRoadShow
                            ? " Per-person pricing is configured separately."
                            : ""}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="status"
                          className="text-sm font-medium text-gray-700"
                        >
                          Status *
                        </Label>
                        <select
                          id="status"
                          value={formData.statusId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              statusId: parseInt(e.target.value),
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          suppressHydrationWarning
                        >
                          {showStatuses
                            .filter(
                              (status) =>
                                status.name === "active" ||
                                status.name === "inactive",
                            )
                            .map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.name === "active"
                                  ? "Active"
                                  : "Inactive"}
                              </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          Set the current status of this show
                        </p>
                      </div>
                    </div>

                    {/* Visibility Date Fields in same section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="visibleFrom"
                          className="text-sm font-medium text-gray-700"
                        >
                          Visible from (optional)
                        </Label>
                        <Input
                          id="visibleFrom"
                          type="date"
                          value={formData.visibleFrom}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              visibleFrom: e.target.value,
                            })
                          }
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Date from which the show becomes visible to customers.
                          Leave empty for always visible.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="visibleUntil"
                          className="text-sm font-medium text-gray-700"
                        >
                          Visible until (optional)
                        </Label>
                        <Input
                          id="visibleUntil"
                          type="date"
                          value={formData.visibleUntil}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              visibleUntil: e.target.value,
                            })
                          }
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Date until which the show remains visible to
                          customers. Leave empty for no end date.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Accounting Article Mapping - Corporate customers and road
                      shows. Road shows are customer-type agnostic but are always
                      invoiced, so they need an article mapping too. */}
                  {(formData.customerTypeId === 2 ||
                    formData.showType === "on_road") && (
                    <AccountingArticleMapping
                      venueId={parseInt(venueId)}
                      itemId={show.id}
                      itemType="show"
                      itemName={formData.name[enabledLanguages[0]] || ""}
                      currentArticleCode={show.article_code || null}
                      accountingId={venue?.accounting_id}
                      userRoleId={userRoleId}
                      preserveData={{
                        studioAssignments: formData.selectedStudios.map(
                          (studioId) => ({
                            studio_id: parseInt(studioId),
                          }),
                        ),
                      }}
                    />
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={saveBasicDetails}
                      disabled={
                        isSavingBasicDetails ||
                        formData.minParticipants <= 0 ||
                        formData.maxParticipants <= 0 ||
                        (formData.showType !== "on_road" &&
                          formData.selectedStudios.length === 0)
                      }
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isSavingBasicDetails ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />{" "}
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" /> Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === "multilingual" && (
                <div className="space-y-6">
                  {/* Language Tabs */}
                  <div className="border-b">
                    <nav className="-mb-px flex space-x-8 flex-wrap">
                      {enabledLanguages.map((langCode, index) => {
                        const language = AVAILABLE_LANGUAGES.find(
                          (l) => l.code === langCode,
                        );
                        return (
                          <div
                            key={langCode}
                            className="flex items-center gap-2"
                          >
                            <button
                              type="button"
                              onClick={() => setActiveLanguageTab(langCode)}
                              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeLanguageTab === langCode
                                  ? "border-purple-600 text-purple-600"
                                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                {language?.flag}{" "}
                                {language?.name || langCode.toUpperCase()}
                                {langCode === venue?.default_language && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs ml-1"
                                  >
                                    Primary
                                  </Badge>
                                )}
                              </span>
                            </button>
                          </div>
                        );
                      })}
                    </nav>
                  </div>

                  {/* Language Tab Content */}
                  <div className="border rounded-lg p-6 space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label
                          htmlFor="name"
                          className="text-sm font-medium text-gray-700"
                        >
                          Show name *
                        </Label>
                        <span
                          className={`text-xs ${(formData.name[activeLanguageTab]?.length || 0) > 25 ? "text-red-500" : "text-gray-400"}`}
                        >
                          {formData.name[activeLanguageTab]?.length || 0}/25
                        </span>
                      </div>
                      <Input
                        id="name"
                        value={formData.name[activeLanguageTab] || ""}
                        maxLength={25}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            name: {
                              ...formData.name,
                              [activeLanguageTab]: e.target.value,
                            },
                          })
                        }
                        placeholder="e.g., Mystery Mansion"
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label
                          htmlFor="subtitle"
                          className="text-sm font-medium text-gray-700"
                        >
                          Subtitle
                        </Label>
                        <span
                          className={`text-xs ${(formData.subtitle[activeLanguageTab]?.length || 0) > 100 ? "text-red-500" : "text-gray-400"}`}
                        >
                          {formData.subtitle[activeLanguageTab]?.length || 0}
                          /100
                        </span>
                      </div>
                      <Input
                        id="subtitle"
                        value={formData.subtitle[activeLanguageTab] || ""}
                        maxLength={100}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subtitle: {
                              ...formData.subtitle,
                              [activeLanguageTab]: e.target.value,
                            },
                          })
                        }
                        placeholder="e.g., An Interactive Murder Mystery Experience"
                        className="w-full"
                      />
                    </div>

                    {/* Description HTML Editor with Side-by-Side Preview */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Description
                      </Label>
                      <div className="grid grid-cols-2 gap-4">
                        {/* HTML Editor Column */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">
                            HTML Editor
                          </div>
                          <Textarea
                            id="description"
                            value={
                              formData.description[activeLanguageTab] || ""
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                description: {
                                  ...formData.description,
                                  [activeLanguageTab]: e.target.value,
                                },
                              })
                            }
                            placeholder="Enter HTML content here. Example:&#10;&#10;&lt;h2&gt;About This Show&lt;/h2&gt;&#10;&lt;p&gt;Experience an amazing show that will...&lt;/p&gt;&#10;&lt;ul&gt;&#10;  &lt;li&gt;Feature 1&lt;/li&gt;&#10;  &lt;li&gt;Feature 2&lt;/li&gt;&#10;&lt;/ul&gt;"
                            className="font-mono text-sm resize-none"
                            style={{ height: "400px" }}
                            spellCheck={false}
                          />
                          <p className="text-xs text-muted-foreground">
                            Write raw HTML. Supported tags: p, h1-h6, strong,
                            em, ul, ol, li, a, br, div, span.
                          </p>
                        </div>

                        {/* Preview Column */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">
                            Live Preview
                          </div>
                          <div
                            className="border rounded-lg p-4 bg-white overflow-y-auto"
                            style={{ height: "400px" }}
                          >
                            {formData.description[activeLanguageTab] ? (
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    formData.description[activeLanguageTab] ||
                                    "",
                                }}
                                style={{
                                  // Add basic styling for HTML elements
                                  lineHeight: "1.6",
                                }}
                              />
                            ) : (
                              <p className="text-gray-400 italic">
                                Enter HTML content to see preview
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Save Content Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={saveBasicDetails}
                        disabled={
                          isSavingBasicDetails ||
                          !formData.name[availableLanguages[0]] ||
                          (formData.showType !== "on_road" &&
                            formData.selectedStudios.length === 0)
                        }
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {isSavingBasicDetails ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />{" "}
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" /> Save content
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Media Section - Single Image Upload */}
                  <div className="border rounded-lg p-6 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold">
                        Image (
                        {AVAILABLE_LANGUAGES.find(
                          (l) => l.code === activeLanguageTab,
                        )?.name || activeLanguageTab}
                        )
                      </h3>
                      <p className="text-sm text-gray-600">
                        Upload an image for this language
                      </p>
                    </div>

                    <div className="space-y-3">
                      {/* Show current image or upload area */}
                      {mediaItems.find(
                        (item) => item.language === activeLanguageTab,
                      ) ? (
                        // Image preview with visible action buttons
                        <div className="space-y-3">
                          <Card className="relative overflow-hidden w-80 h-80">
                            <div className="w-full h-full bg-gray-100">
                              <img
                                src={
                                  mediaItems.find(
                                    (item) =>
                                      item.language === activeLanguageTab,
                                  )?.url
                                }
                                alt="Show image"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </Card>

                          {/* Always visible action buttons */}
                          <div className="flex gap-3">
                            <label
                              htmlFor={`media-upload-${activeLanguageTab}`}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer"
                            >
                              {isUploadingMedia ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" />
                              )}
                              Replace image
                              <input
                                id={`media-upload-${activeLanguageTab}`}
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Remove existing image for this language first
                                    const existingItem = mediaItems.find(
                                      (item) =>
                                        item.language === activeLanguageTab,
                                    );
                                    if (existingItem) {
                                      removeMedia(existingItem.id);
                                    }
                                    // Create a new FileList-like object and call handleFileSelect
                                    const dataTransfer = new DataTransfer();
                                    dataTransfer.items.add(file);
                                    e.target.files = dataTransfer.files;
                                    handleFileSelect(e);
                                  }
                                }}
                                className="hidden"
                                disabled={isUploadingMedia}
                              />
                            </label>
                            <button
                              onClick={() => {
                                const existingItem = mediaItems.find(
                                  (item) => item.language === activeLanguageTab,
                                );
                                if (existingItem) {
                                  removeMedia(existingItem.id);
                                }
                              }}
                              disabled={isUploadingMedia}
                              className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                              Remove
                            </button>
                          </div>

                          {/* Format info */}
                          <div className="text-xs text-gray-500 space-y-0.5">
                            <p>JPG, PNG, WEBP or GIF (max 10MB)</p>
                            <p>Recommended 4:3 ratio (e.g. 1200x900)</p>
                          </div>
                        </div>
                      ) : (
                        // Upload area when no image
                        <Card
                          className={`border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
                            isDragging
                              ? "border-purple-500 bg-purple-50"
                              : "border-gray-300 hover:border-gray-400"
                          } ${isUploadingMedia ? "opacity-50 pointer-events-none" : ""}`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() =>
                            document
                              .getElementById(
                                `media-upload-empty-${activeLanguageTab}`,
                              )
                              ?.click()
                          }
                        >
                          {isUploadingMedia ? (
                            <div className="space-y-2">
                              <RefreshCw className="h-10 w-10 mx-auto animate-spin text-purple-600" />
                              <p className="text-sm text-gray-600">
                                Uploading image...
                              </p>
                            </div>
                          ) : (
                            <>
                              <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                              <p className="text-sm text-gray-600 mb-2">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                JPG, PNG, WEBP or GIF (max 10MB)
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Recommended 4:3 ratio (e.g. 1200x900)
                              </p>
                            </>
                          )}
                          <input
                            id={`media-upload-empty-${activeLanguageTab}`}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                            disabled={isUploadingMedia}
                          />
                        </Card>
                      )}
                    </div>

                    {/* Save Media Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={saveMedia}
                        disabled={isSavingMedia}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {isSavingMedia ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />{" "}
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" /> Save Image
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "time-slot-pricing" &&
                canTab("time-slot-pricing") && (
                  <TimeSlotPricingTab
                    pricePoints={pricePoints}
                    selectedPricePoint={selectedPricePoint}
                    onPricePointSelect={setSelectedPricePoint}
                    onPricePointCreate={createPricePoint}
                    onPricePointDelete={deletePricePoint}
                    onPricePointStatusUpdate={updatePricePointStatus}
                    onSlotPricingUpdate={saveTimeSlotPricing}
                    uniqueTimes={uniqueTimes}
                    isSaving={isSavingSlots}
                    savingSlots={savingSlotIds}
                    venueId={parseInt(venueId)}
                    showId={show.id}
                    venueCurrency={venue?.currency || "SEK"}
                    venueDefaultLanguage={venueDefaultLanguage}
                    userRoleId={pricingViewRoleId}
                    onUserRoleIdChange={setPricingViewRoleId}
                  />
                )}

              {activeTab === "emails" && canTab("emails") && (
                <EmailsTab venueId={parseInt(venueId)} showId={show.id} />
              )}

              {activeTab === "review-questions" &&
                canTab("review-questions") && (
                  <ShowReviewQuestionsTab
                    venueId={venueId}
                    showId={show.id}
                    languages={availableLanguages}
                    showName={
                      formData.name?.[activeLanguageTab] ||
                      formData.name?.en ||
                      undefined
                    }
                  />
                )}

              {activeTab === "rules" && canTab("rules") && (
                <div className="space-y-6">
                  {/* Booking Modification & Cancellation Rules Section */}
                  <div className="border rounded-lg p-6 space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Booking Modification & Cancellation Rules
                      </h3>
                      <p className="text-sm text-gray-600">
                        Define time-based rules for participant changes and
                        cancellations
                      </p>
                    </div>

                    {/* Participant Change Rules */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-800 border-b pb-2">
                        Participant Change Rules
                      </h4>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label
                            htmlFor="partChangeFullRefundDays"
                            className="text-sm font-medium text-gray-700"
                          >
                            Full refund period *
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="partChangeFullRefundDays"
                              type="number"
                              min="1"
                              value={formData.partChangeFullRefundDays}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  partChangeFullRefundDays:
                                    parseInt(e.target.value) || 7,
                                })
                              }
                              className="w-full"
                            />
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              days
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Days before show for full refund on participant
                            changes
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor="partChangeCutoffHours"
                            className="text-sm font-medium text-gray-700"
                          >
                            Information-only cutoff *
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="partChangeCutoffHours"
                              type="number"
                              min="1"
                              value={formData.partChangeCutoffHours}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  partChangeCutoffHours:
                                    parseInt(e.target.value) || 5,
                                })
                              }
                              className="w-full"
                            />
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              hours
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Hours before show when only informational changes
                            allowed
                          </p>
                        </div>
                      </div>

                      {/* Info boxes for periods */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-sm">
                        <p className="text-blue-900 font-medium">
                          📋 Change Periods:
                        </p>
                        <ul className="text-blue-800 space-y-1 ml-4">
                          <li>
                            • <strong>Period 1</strong> (
                            {`> ${formData.partChangeFullRefundDays} days`}):
                            Full refunds for participant decreases
                          </li>
                          <li>
                            • <strong>Period 2</strong> (
                            {formData.partChangeFullRefundDays} days -{" "}
                            {formData.partChangeCutoffHours} hours): Info
                            changes only, addon refunds
                          </li>
                          <li>
                            • <strong>Period 3</strong> (
                            {`< ${formData.partChangeCutoffHours} hours`}): NO
                            changes allowed
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Date/Time Modification Rules */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-800 border-b pb-2">
                        Date/Time Modification Rules
                      </h4>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label
                            htmlFor="changeTimeslotDays"
                            className="text-sm font-medium text-gray-700"
                          >
                            Date/time change window *
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="changeTimeslotDays"
                              type="number"
                              min="0"
                              value={formData.changeTimeslotDays}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  changeTimeslotDays:
                                    e.target.value === ""
                                      ? 7
                                      : parseInt(e.target.value) || 0,
                                })
                              }
                              className="w-full"
                            />
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              days
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Minimum days before show that a customer can change
                            the booking date/time
                          </p>
                        </div>
                      </div>

                      {/* Info box for date/time modification */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2 text-sm">
                        <p className="text-amber-900 font-medium">
                          🗓️ Date/Time Change Window:
                        </p>
                        <ul className="text-amber-800 space-y-1 ml-4">
                          <li>
                            • <strong>Allowed:</strong>{" "}
                            {`> ${formData.changeTimeslotDays} days`} before
                            show
                          </li>
                          <li>
                            • <strong>Blocked:</strong>{" "}
                            {`< ${formData.changeTimeslotDays} days`} before
                            show — customer cannot reschedule
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Cancellation Rules */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-800 border-b pb-2">
                        Cancellation Rules
                      </h4>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label
                            htmlFor="cancelFreeDays"
                            className="text-sm font-medium text-gray-700"
                          >
                            Free cancellation window *
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="cancelFreeDays"
                              type="number"
                              min="0"
                              value={formData.cancelFreeDays}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  cancelFreeDays: parseInt(e.target.value) || 7,
                                })
                              }
                              className="w-full"
                            />
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              days
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Days before show for free cancellation
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor="cancelGraceHours"
                            className="text-sm font-medium text-gray-700"
                          >
                            Grace period after booking *
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="cancelGraceHours"
                              type="number"
                              min="0"
                              value={formData.cancelGraceHours}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  cancelGraceHours:
                                    parseInt(e.target.value) || 24,
                                })
                              }
                              className="w-full"
                            />
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              hours
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Hours after booking creation for free cancellation
                            (grace)
                          </p>
                        </div>
                      </div>

                      {/* Info boxes for cancellation */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm">
                        <p className="text-green-900 font-medium">
                          ✅ Cancellation Windows:
                        </p>
                        <ul className="text-green-800 space-y-1 ml-4">
                          <li>
                            • <strong>Standard:</strong> Free cancel{" "}
                            {`> ${formData.cancelFreeDays} days`} before show
                          </li>
                          <li>
                            • <strong>Grace:</strong> Free cancel within{" "}
                            {formData.cancelGraceHours} hours of booking (any
                            time)
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={saveRules}
                      disabled={isSavingRules}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isSavingRules ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />{" "}
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" /> Save Rules
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === "faqs" && canTab("faqs") && (
                <ShowFAQsTab
                  venueId={venueId}
                  showId={show.id.toString()}
                  availableLanguages={availableLanguages}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
